import { useState } from "react";

const POSITIONS = [
  "Pitcher", "Catcher", "First Base", "Second Base", "Third Base",
  "Shortstop", "Left Field", "Center Field", "Right Field", "Utility", "Designated Hitter"
];

const SKILL_LEVELS = ["AA", "AAA", "Majors", "JV", "Varsity", "Premier", "Collegiate"];

const GRAD_YEARS = Array.from({ length: 10 }, (_, i) => 2026 + i);

const WAIVER_TEXT = `ASSUMPTION OF RISK AND WAIVER OF LIABILITY

I, the undersigned parent/legal guardian, hereby acknowledge and agree to the following terms on behalf of the participant:

1. ASSUMPTION OF RISK: I understand that participation in baseball/softball training activities involves inherent risks including but not limited to: physical contact, ball impacts, muscle strains, sprains, fractures, concussions, and other injuries. I voluntarily assume all risks associated with participation.

2. WAIVER AND RELEASE: I hereby release, waive, and discharge A3 Academy, its owners, coaches, staff, volunteers, and affiliates from any and all liability, claims, demands, and causes of action arising from participation in training activities.

3. MEDICAL AUTHORIZATION: In the event of an emergency, I authorize A3 Academy staff to seek appropriate medical treatment for the participant. I understand that I am responsible for all medical costs incurred.

4. MEDIA RELEASE: I grant permission for A3 Academy to use photographs and videos of the participant for promotional purposes including social media, website, and marketing materials.

5. FACILITY RULES: I agree that the participant will follow all facility rules, coach instructions, and safety guidelines. A3 Academy reserves the right to dismiss any participant for misconduct without refund.

6. PAYMENT TERMS: I understand and agree to the following:
   • $1,300 per month of training (June or July) or $2,500 for both months
   • $350 deposit due at registration, applied toward the total
   • Remaining balance due two weeks before each month begins
   • Option to pay in full at registration
   • Makeup days will be scheduled the first week of August
   Failure to make scheduled payments may result in suspension from training until the account is current.

By signing below, I confirm that I have read, understand, and agree to all terms above.`;

// ── Stripe Payment Links ──
// Routed by campus so funds land in the correct Stripe → checking account.
const STRIPE_LINKS = {
  deposit: {
    Tampa: "https://buy.stripe.com/4gM8wPfvr4oPfLD6DJbQY0S",
    Jacksonville: "https://buy.stripe.com/eVqeV6258cM2c9s9VZ1kA0H",
  },
  full: {
    Tampa: {
      June: "https://buy.stripe.com/bJeaEXab77B1bvnbY3bQY12",
      July: "https://buy.stripe.com/fZu4gz2IFbRh7f77HNbQY13",
      Both: "https://buy.stripe.com/5kQbJ1ern9J9arj4vBbQY11",
    },
    Jacksonville: {
      June: "https://buy.stripe.com/bJe5kwcJMh2iflE0lp1kA0P",
      July: "https://buy.stripe.com/dRm00c6lodQ62yS7NR1kA0Q",
      Both: "https://buy.stripe.com/8x26oA7ps13k5L49VZ1kA0N",
    },
  },
};

const PRICING = { June: 1300, July: 1300, Both: 2500 };
const DEPOSIT_AMOUNT = 350;

const LOCATIONS = [
  { campus: "Tampa", address: "4711 N Manhattan Avenue, Tampa, FL" },
  { campus: "Jacksonville", address: "11243 Distribution Avenue, Jacksonville, FL" },
];

const FAQ_ITEMS = [
  { q: "When does training start?", a: "June 1 is the first day." },
  { q: "What's the youngest age you accept?", a: "9 years old." },
  { q: "If I select 4 weeks, can I customize which weeks — say 2 in June and 2 in July around tournaments and vacations?", a: "Yes. We'll send out a survey so you can pick the specific weeks your son will attend." },
  { q: "If my sons are different ages — say 14 and 10 — can they train together at 9 AM?", a: "Yes." },
  { q: "How are players broken up during training?", a: "By skill and age level into cages and groups together." },
  { q: "Will my son receive pitching instruction?", a: "We can organize bullpens and help with one bullpen per week, but this training is geared toward hitting, athleticism, speed and strength primarily." },
  { q: "What should my son wear to training?", a: "Shorts, sneakers, training gear." },
  { q: "Should he bring a lunch?", a: "He can bring a lunch — we have refrigerators and microwaves so he can heat it up." },
  { q: "How early can I drop him off?", a: "8:30 AM." },
  { q: "How late can I pick him up?", a: "3:00 PM." },
];

function SelectField({ label, value, onChange, options, placeholder, required }) {
  return (
    <div style={styles.fieldGroup}>
      <label style={styles.label}>{label} {required && <span style={styles.req}>*</span>}</label>
      <select style={styles.select} value={value} onChange={e => onChange(e.target.value)} required={required}>
        <option value="">{placeholder || "Select..."}</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

function TextField({ label, value, onChange, type = "text", placeholder, required }) {
  return (
    <div style={styles.fieldGroup}>
      <label style={styles.label}>{label} {required && <span style={styles.req}>*</span>}</label>
      <input
        style={styles.input}
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
      />
    </div>
  );
}

function RadioGroup({ label, value, onChange, options, required }) {
  return (
    <div style={styles.fieldGroup}>
      <label style={styles.label}>{label} {required && <span style={styles.req}>*</span>}</label>
      <div style={styles.radioRow}>
        {options.map(o => (
          <button
            key={o}
            type="button"
            onClick={() => onChange(o)}
            style={{
              ...styles.radioBtn,
              ...(value === o ? styles.radioBtnActive : {}),
            }}
          >
            {o}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function A3Registration() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    playerName: "", dob: "", gradYear: "", primaryPosition: "",
    secondaryPosition: "", bats: "", throws: "", skillLevel: "",
    campus: "", months: "", currentSchool: "", parentName: "",
    parentEmail: "", parentPhone: "",
  });
  const [paymentOption, setPaymentOption] = useState("deposit");
  const [waiverSigned, setWaiverSigned] = useState(false);
  const [signatureName, setSignatureName] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [showLanding, setShowLanding] = useState(true);

  const set = (key) => (val) => setForm(f => ({ ...f, [key]: val }));

  const isStep0Valid = form.playerName && form.dob && form.gradYear &&
    form.primaryPosition && form.bats && form.throws &&
    form.skillLevel && form.campus && form.months && form.currentSchool &&
    form.parentName && form.parentEmail && form.parentPhone;

  const isStep1Valid = waiverSigned && signatureName.length > 2;

  // ── Derived pricing (recalculated each render) ──
  const totalCost = PRICING[form.months] || 0;
  const amountDueToday = paymentOption === "full" ? totalCost : DEPOSIT_AMOUNT;

  const scheduleItems = (() => {
    if (!form.months) return [];
    if (paymentOption === "full") {
      return [
        { date: "Today — Pay in Full", amount: totalCost, note: "Full payment at registration", primary: true },
      ];
    }
    const items = [
      { date: "Today — Deposit", amount: DEPOSIT_AMOUNT, note: "Due now to secure your spot", primary: true },
    ];
    if (form.months === "June") {
      items.push({ date: "May 15, 2026", amount: 950, note: "Balance — June training" });
    } else if (form.months === "July") {
      items.push({ date: "June 15, 2026", amount: 950, note: "Balance — July training" });
    } else if (form.months === "Both") {
      items.push({ date: "May 15, 2026", amount: 900, note: "Balance — June training" });
      items.push({ date: "June 15, 2026", amount: 1250, note: "July training" });
    }
    return items;
  })();

  // ── Google Sheets Integration ──
  // Replace this URL with your Google Apps Script Web App URL (see setup instructions)
  const SHEETS_URL = "https://script.google.com/macros/s/AKfycbyoD-Gwp3auyuD40Pm3wo1k-v2GNH1XXg15hLtJVJnQs7govazUrT4MLjpAaDIyEo2ZtA/exec";

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError("");

    const payload = {
      timestamp: new Date().toLocaleString(),
      playerName: form.playerName,
      dob: form.dob,
      gradYear: form.gradYear,
      primaryPosition: form.primaryPosition,
      secondaryPosition: form.secondaryPosition || "N/A",
      bats: form.bats,
      throws: form.throws,
      skillLevel: form.skillLevel,
      campus: form.campus,
      months: form.months,
      currentSchool: form.currentSchool,
      parentName: form.parentName,
      parentEmail: form.parentEmail,
      parentPhone: form.parentPhone,
      waiverSigned: "Yes",
      signatureName: signatureName,
      paymentOption: paymentOption === "full" ? "Pay in Full" : "Deposit",
      amountDueToday: `$${amountDueToday.toLocaleString()}`,
      scheduleDetail: scheduleItems
        .map(it => `${it.date}: $${it.amount.toLocaleString()}`)
        .join(" | "),
      totalAmount: `$${totalCost.toLocaleString()}`,
      depositStatus: "Pending",
    };

    try {
      await fetch(SHEETS_URL, {
        method: "POST",
        mode: "no-cors",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      setSubmitted(true);
    } catch (err) {
      console.error("Sheet submit error:", err);
      // Still allow them to proceed to payment even if sheets fails
      setSubmitted(true);
      setSubmitError("Registration saved locally. Google Sheets sync may be delayed.");
    } finally {
      setSubmitting(false);
    }
  };

  const stripeLink = (() => {
    if (!form.campus || !form.months) return "#";
    if (paymentOption === "full") {
      return STRIPE_LINKS.full[form.campus]?.[form.months] || "#";
    }
    return STRIPE_LINKS.deposit[form.campus] || "#";
  })();

  // ── STEP CONTENT ──
  const stepContent = [
    // STEP 0 — Player Info
    () => (
      <div style={styles.stepInner}>
        <div style={styles.sectionLabel}>Player Information</div>
        <TextField label="Player Full Name" value={form.playerName} onChange={set("playerName")} placeholder="First Last" required />
        <div style={styles.row}>
          <TextField label="Date of Birth" value={form.dob} onChange={set("dob")} type="date" required />
          <SelectField label="Grad Year" value={form.gradYear} onChange={set("gradYear")} options={GRAD_YEARS.map(String)} required />
        </div>
        <div style={styles.row}>
          <SelectField label="Primary Position" value={form.primaryPosition} onChange={set("primaryPosition")} options={POSITIONS} required />
          <SelectField label="Secondary Position" value={form.secondaryPosition} onChange={set("secondaryPosition")} options={POSITIONS} />
        </div>
        <div style={styles.row}>
          <RadioGroup label="Bats" value={form.bats} onChange={set("bats")} options={["Right", "Left", "Switch"]} required />
          <RadioGroup label="Throws" value={form.throws} onChange={set("throws")} options={["Right", "Left", "Switch"]} required />
        </div>
        <SelectField label="Skill Level" value={form.skillLevel} onChange={set("skillLevel")} options={SKILL_LEVELS} required />

        <div style={styles.sectionLabel}>Campus & School</div>
        <RadioGroup label="Campus" value={form.campus} onChange={set("campus")} options={["Tampa", "Jacksonville"]} required />
        <TextField label="Current School" value={form.currentSchool} onChange={set("currentSchool")} placeholder="School name" required />

        <div style={styles.sectionLabel}>Training Session</div>
        <RadioGroup label="Months" value={form.months} onChange={set("months")} options={["June", "July", "Both"]} required />
        <div style={styles.helperText}>
          $1,300/month · $2,500 for both (save $100) · Makeup days the first week of August
        </div>

        <div style={styles.sectionLabel}>Parent / Guardian</div>
        <TextField label="Parent/Guardian Name" value={form.parentName} onChange={set("parentName")} placeholder="Full name" required />
        <div style={styles.row}>
          <TextField label="Email" value={form.parentEmail} onChange={set("parentEmail")} type="email" placeholder="email@example.com" required />
          <TextField label="Phone" value={form.parentPhone} onChange={set("parentPhone")} type="tel" placeholder="(555) 123-4567" required />
        </div>

        <button
          style={{ ...styles.primaryBtn, ...(isStep0Valid ? {} : styles.btnDisabled) }}
          disabled={!isStep0Valid}
          onClick={() => setStep(1)}
        >
          Continue to Waiver →
        </button>
      </div>
    ),

    // STEP 1 — Waiver
    () => (
      <div style={styles.stepInner}>
        <div style={styles.sectionLabel}>Liability Waiver & Terms</div>
        <div style={styles.waiverBox}>
          <pre style={styles.waiverText}>{WAIVER_TEXT}</pre>
        </div>
        <div style={styles.checkRow} onClick={() => setWaiverSigned(!waiverSigned)}>
          <div style={{ ...styles.checkbox, ...(waiverSigned ? styles.checkboxChecked : {}) }}>
            {waiverSigned && <span style={{ color: "#fff", fontSize: 14, lineHeight: 1 }}>✓</span>}
          </div>
          <span style={styles.checkLabel}>
            I have read and agree to the Assumption of Risk and Waiver of Liability
          </span>
        </div>
        <TextField
          label="Digital Signature (Parent/Guardian Full Name)"
          value={signatureName}
          onChange={setSignatureName}
          placeholder="Type your full legal name"
          required
        />
        <div style={styles.btnRow}>
          <button style={styles.secondaryBtn} onClick={() => setStep(0)}>← Back</button>
          <button
            style={{ ...styles.primaryBtn, ...(isStep1Valid ? {} : styles.btnDisabled) }}
            disabled={!isStep1Valid}
            onClick={() => { setStep(2); }}
          >
            Continue to Payment →
          </button>
        </div>
      </div>
    ),

    // STEP 2 — Payment
    () => (
      <div style={styles.stepInner}>
        <div style={styles.sectionLabel}>Payment Summary</div>
        <div style={styles.summaryCard}>
          <div style={styles.summaryRow}>
            <span style={styles.summaryLabel}>Player</span>
            <span style={styles.summaryValue}>{form.playerName}</span>
          </div>
          <div style={styles.summaryRow}>
            <span style={styles.summaryLabel}>Campus</span>
            <span style={{ ...styles.summaryValue, ...styles.campusBadge, background: form.campus === "Tampa" ? colors.accentDeep : colors.accent }}>
              {form.campus}
            </span>
          </div>
          <div style={styles.summaryRow}>
            <span style={styles.summaryLabel}>Skill Level</span>
            <span style={styles.summaryValue}>{form.skillLevel}</span>
          </div>
          <div style={styles.summaryRow}>
            <span style={styles.summaryLabel}>Months</span>
            <span style={styles.summaryValue}>
              {form.months === "Both" ? "June & July" : `${form.months} Only`}
            </span>
          </div>
          <div style={{ ...styles.summaryRow, borderBottom: "none" }}>
            <span style={styles.summaryLabel}>Program Cost</span>
            <span style={styles.summaryValue}>
              ${totalCost.toLocaleString()}
              {form.months === "Both" && (
                <span style={{ fontSize: 12, color: colors.textMuted, marginLeft: 8, fontWeight: 500 }}>
                  (save $100)
                </span>
              )}
            </span>
          </div>
        </div>

        <div style={styles.sectionLabel}>Payment Option</div>
        <div style={styles.paymentToggle}>
          <button
            type="button"
            onClick={() => setPaymentOption("deposit")}
            style={{
              ...styles.paymentOptionBtn,
              ...(paymentOption === "deposit" ? styles.paymentOptionBtnActive : {}),
            }}
          >
            <div style={styles.paymentOptionLabel}>Deposit</div>
            <div style={styles.paymentOptionAmount}>${DEPOSIT_AMOUNT.toLocaleString()}</div>
            <div style={styles.paymentOptionNote}>Secure spot · Balance scheduled</div>
          </button>
          <button
            type="button"
            onClick={() => setPaymentOption("full")}
            style={{
              ...styles.paymentOptionBtn,
              ...(paymentOption === "full" ? styles.paymentOptionBtnActive : {}),
            }}
          >
            <div style={styles.paymentOptionLabel}>Pay in Full</div>
            <div style={styles.paymentOptionAmount}>${totalCost.toLocaleString()}</div>
            <div style={styles.paymentOptionNote}>Done in one payment</div>
          </button>
        </div>

        <div style={styles.sectionLabel}>Payment Schedule</div>
        <div style={styles.paymentTimeline}>
          {scheduleItems.map((item, idx) => (
            <div key={idx}>
              {idx > 0 && <div style={styles.timelineConnector} />}
              <div style={styles.timelineItem}>
                <div style={{ ...styles.timelineDot, ...(item.primary ? { background: colors.accent } : {}) }} />
                <div style={styles.timelineContent}>
                  <div style={styles.timelineDate}>{item.date}</div>
                  <div style={styles.timelineAmount}>${item.amount.toLocaleString()}.00</div>
                  <div style={styles.timelineNote}>{item.note}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div style={styles.totalBox}>
          <span>Total Program Cost</span>
          <span style={styles.totalAmount}>${totalCost.toLocaleString()}.00</span>
        </div>

        <div style={styles.infoBox}>
          <strong style={{ color: colors.accent }}>Note:</strong> Makeup days will be scheduled the first week of August.
        </div>

        {!submitted ? (
          <div style={styles.btnRow}>
            <button style={styles.secondaryBtn} onClick={() => setStep(1)}>← Back</button>
            <button
              style={{ ...styles.stripeBtn, ...(submitting ? { opacity: 0.6 } : {}) }}
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? (
                "Saving Registration..."
              ) : (
                <>
                  <svg width="20" height="20" viewBox="0 0 32 32" fill="none" style={{ marginRight: 8 }}>
                    <rect width="32" height="32" rx="6" fill="#635BFF"/>
                    <path d="M15.2 13.2c0-.8.7-1.1 1.8-1.1 1.6 0 3.6.5 5.2 1.4V9.2c-1.7-.7-3.5-1-5.2-1-4.3 0-7.1 2.2-7.1 6 0 5.8 8 4.9 8 7.4 0 .9-.8 1.2-1.9 1.2-1.7 0-3.8-.7-5.5-1.6v4.4c1.9.8 3.8 1.2 5.5 1.2 4.4 0 7.3-2.1 7.3-5.9-.1-6.3-8.1-5.2-8.1-7.7z" fill="#fff"/>
                  </svg>
                  Pay ${amountDueToday.toLocaleString()} {paymentOption === "full" ? "in Full" : "Deposit"}
                </>
              )}
            </button>
          </div>
        ) : (
          <div style={styles.successBox}>
            <div style={styles.successIcon}>✓</div>
            <div style={styles.successTitle}>Registration Saved!</div>
            {submitError && <p style={{ color: colors.accentSoft, fontSize: 13, marginBottom: 12 }}>{submitError}</p>}
            <p style={styles.successText}>
              Your information has been recorded. Click below to complete your{" "}
              <strong>${amountDueToday.toLocaleString()} {paymentOption === "full" ? "full payment" : "deposit"}</strong> via Stripe.
            </p>
            <a
              href={stripeLink}
              target="_blank"
              rel="noopener noreferrer"
              style={styles.stripeBtn}
            >
              <svg width="20" height="20" viewBox="0 0 32 32" fill="none" style={{ marginRight: 8 }}>
                <rect width="32" height="32" rx="6" fill="#635BFF"/>
                <path d="M15.2 13.2c0-.8.7-1.1 1.8-1.1 1.6 0 3.6.5 5.2 1.4V9.2c-1.7-.7-3.5-1-5.2-1-4.3 0-7.1 2.2-7.1 6 0 5.8 8 4.9 8 7.4 0 .9-.8 1.2-1.9 1.2-1.7 0-3.8-.7-5.5-1.6v4.4c1.9.8 3.8 1.2 5.5 1.2 4.4 0 7.3-2.1 7.3-5.9-.1-6.3-8.1-5.2-8.1-7.7z" fill="#fff"/>
              </svg>
              Complete ${amountDueToday.toLocaleString()} {paymentOption === "full" ? "Payment" : "Deposit"} on Stripe →
            </a>
            {paymentOption === "deposit" && scheduleItems.length > 1 && (
              <p style={styles.reminderText}>
                You will receive a Stripe invoice for the remaining balance:{" "}
                {scheduleItems.slice(1).map((it, i, arr) => (
                  <span key={i}>
                    <strong>${it.amount.toLocaleString()} on {it.date}</strong>
                    {i < arr.length - 1 ? " and " : ""}
                  </span>
                ))}
                .
              </p>
            )}
          </div>
        )}
      </div>
    ),
  ];

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.logoRow}>
            <img src="/pics/a3-logo.png" alt="A3 Academy" style={styles.logoMark} />
            <div style={styles.brandText}>
              <div style={styles.brandSub}>SUMMER TRAINING</div>
              <div style={styles.brandYear}>2026</div>
            </div>
          </div>
          <div style={styles.headerTagline}>June & July · Tampa & Jacksonville</div>
        </div>

        {showLanding ? (
          /* Landing / Program Overview */
          <>
            <div style={styles.heroSection}>
              <div style={styles.heroOverlay}>
                <div style={styles.heroTagline}>Bigger. Stronger. Faster.</div>
                <div style={styles.heroLead}>
                  Eight weeks of elite baseball and athletic development for the next generation of ballers.
                </div>
              </div>
            </div>

            <div style={styles.landingSectionTitle}>The Schedule</div>
            <div style={styles.factsGrid}>
              <div style={styles.factCard}>
                <div style={styles.factLabel}>Ages 9–13</div>
                <div style={styles.factValue}>9 AM – 12 PM</div>
              </div>
              <div style={styles.factCard}>
                <div style={styles.factLabel}>Ages 14+</div>
                <div style={styles.factValue}>12 PM – 3 PM</div>
              </div>
              <div style={styles.factCard}>
                <div style={styles.factLabel}>Days</div>
                <div style={styles.factValue}>Mon · Tue · Thu · Fri</div>
                <div style={styles.factSub}>Wednesdays off</div>
              </div>
              <div style={styles.factCard}>
                <div style={styles.factLabel}>Campuses</div>
                <div style={styles.factValue}>Tampa · Jacksonville</div>
              </div>
            </div>

            <div style={styles.landingSectionTitle}>Daily Training · 3 Hours</div>
            <div style={styles.trainingGrid}>
              <div style={styles.trainingCard}>
                <div style={{ ...styles.trainingImage, backgroundImage: "url(/pics/DSC04320.JPG)" }} />
                <div style={styles.trainingCardBody}>
                  <div style={styles.trainingDuration}>90 MIN</div>
                  <div style={styles.trainingTitle}>Baseball</div>
                  <div style={styles.trainingDescription}>
                    The program is mostly hitting, with some days dedicated to throwing and glove work.
                  </div>
                </div>
              </div>
              <div style={styles.trainingCard}>
                <div style={{ ...styles.trainingImage, backgroundImage: "url(/pics/DSC04164.JPG)" }} />
                <div style={styles.trainingCardBody}>
                  <div style={styles.trainingDuration}>90 MIN</div>
                  <div style={styles.trainingTitle}>Strength & Performance</div>
                  <div style={styles.trainingDescription}>
                    Speed, agility, strength, and athleticism — built specifically for young athletes by B3 Performance coaches.
                  </div>
                </div>
              </div>
            </div>

            <div style={styles.landingSectionTitle}>Training In Action</div>
            <div style={styles.galleryGrid}>
              <div style={{ ...styles.galleryItem, backgroundImage: "url(/pics/DSC07392.JPG)" }} />
              <div style={{ ...styles.galleryItem, backgroundImage: "url(/pics/DSC06936.JPG)" }} />
              <div style={{ ...styles.galleryItem, backgroundImage: "url(/pics/DSC06948.JPG)" }} />
            </div>

            <div style={styles.landingSectionTitle}>Choose Your Program</div>
            <div style={styles.pricingGrid}>
              <div style={styles.pricingCard}>
                <div style={styles.pricingDuration}>4 Weeks</div>
                <div style={styles.pricingMonth}>June</div>
                <div style={styles.pricingPrice}>$1,300</div>
                <div style={styles.pricingRate}>$325 / week</div>
              </div>
              <div style={styles.pricingCard}>
                <div style={styles.pricingDuration}>4 Weeks</div>
                <div style={styles.pricingMonth}>July</div>
                <div style={styles.pricingPrice}>$1,300</div>
                <div style={styles.pricingRate}>$325 / week</div>
              </div>
              <div style={{ ...styles.pricingCard, ...styles.pricingCardFeatured }}>
                <div style={styles.pricingBadge}>Best Value</div>
                <div style={styles.pricingDuration}>8 Weeks</div>
                <div style={styles.pricingMonth}>June + July</div>
                <div style={styles.pricingPrice}>$2,500</div>
                <div style={styles.pricingRate}>Save $100</div>
              </div>
            </div>
            <div style={styles.landingFinePrint}>
              Makeup days will be scheduled the first week of August.
            </div>

            <div style={styles.ctaCard}>
              <div style={styles.ctaUrgency}>Spots Are Limited</div>
              <div style={styles.ctaSubtext}>
                Secure your spot today with a $350 deposit. Balance scheduled before training begins, or pay in full and you're done.
              </div>
              <button style={styles.ctaButton} onClick={() => setShowLanding(false)}>
                Register Now →
              </button>
            </div>

            <div style={styles.landingSectionTitle}>Locations</div>
            <div style={styles.locationsGrid}>
              {LOCATIONS.map((loc) => (
                <div key={loc.campus} style={styles.locationCard}>
                  <div style={styles.locationCampus}>{loc.campus}</div>
                  <div style={styles.locationAddress}>{loc.address}</div>
                </div>
              ))}
            </div>

            <div style={styles.landingSectionTitle}>FAQ</div>
            <div style={styles.faqList}>
              {FAQ_ITEMS.map((item, i) => (
                <div
                  key={i}
                  style={{
                    ...styles.faqItem,
                    ...(i === FAQ_ITEMS.length - 1 ? { borderBottom: "none" } : {}),
                  }}
                >
                  <div style={styles.faqQuestion}>{item.q}</div>
                  <div style={styles.faqAnswer}>{item.a}</div>
                </div>
              ))}
            </div>

            <div style={styles.policyNotice}>
              A3 reserves the right to remove any player from summer training who doesn't have the maturity level to behave, train, and not be a distraction to the group. A3 Summer Training is not a day care or summer camp — this is for players who want to get better and are dedicated to baseball.
            </div>
          </>
        ) : (
          <>
            {/* Stepper */}
            <div style={styles.stepper}>
              {["Player Info", "Waiver", "Payment"].map((s, i) => (
                <div key={s} style={styles.stepRow}>
                  <div style={{
                    ...styles.stepCircle,
                    ...(i <= step ? styles.stepCircleActive : {}),
                    ...(i < step ? styles.stepCircleDone : {}),
                  }}>
                    {i < step ? "✓" : i + 1}
                  </div>
                  <span style={{
                    ...styles.stepLabel,
                    ...(i <= step ? styles.stepLabelActive : {}),
                  }}>{s}</span>
                  {i < 2 && <div style={{
                    ...styles.stepLine,
                    ...(i < step ? styles.stepLineActive : {}),
                  }} />}
                </div>
              ))}
            </div>

            {/* Form Steps */}
            {stepContent[step]()}
          </>
        )}

        {/* Footer */}
        <div style={styles.footer}>
          A3 Academy · Tampa & Jacksonville, FL · Summer 2026
        </div>
      </div>
    </div>
  );
}

// ── STYLES ──
const colors = {
  bg: "#050a14",
  card: "#0d1929",
  cardBorder: "#1a2a47",
  accent: "#2d70d0",
  accentDeep: "#0d2552",
  accentSoft: "#a8c8e8",
  accentGlow: "rgba(45, 112, 208, 0.18)",
  text: "#e8edf5",
  textMuted: "#8a96aa",
  input: "#0f1d33",
  inputBorder: "#2a4170",
  inputFocus: "#2d70d0",
  stripe: "#635BFF",
  success: "#2ecc71",
};

const font = "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif";

const styles = {
  page: {
    minHeight: "100vh",
    background: colors.bg,
    fontFamily: font,
    color: colors.text,
    padding: "24px 16px",
  },
  container: {
    maxWidth: 620,
    margin: "0 auto",
  },

  // Header
  header: {
    textAlign: "center",
    marginBottom: 32,
    paddingBottom: 24,
    borderBottom: `1px solid ${colors.cardBorder}`,
  },
  logoRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 14,
    marginBottom: 8,
  },
  logoMark: {
    width: 96,
    height: 96,
    objectFit: "contain",
    display: "block",
    clipPath: "circle(48% at center)",
  },
  brandText: {
    textAlign: "left",
    lineHeight: 1,
  },
  brandSub: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 4,
    color: colors.accentSoft,
    marginBottom: 4,
  },
  brandYear: {
    fontSize: 36,
    fontWeight: 900,
    letterSpacing: 2,
    color: colors.text,
    lineHeight: 1,
    textShadow: `0 0 24px ${colors.accentGlow}`,
  },
  headerTagline: {
    fontSize: 14,
    color: colors.textMuted,
    marginTop: 10,
  },

  // Stepper
  stepper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 0,
    marginBottom: 28,
    padding: "16px 0",
  },
  stepRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  stepCircle: {
    width: 30,
    height: 30,
    borderRadius: "50%",
    border: `2px solid ${colors.inputBorder}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    fontWeight: 700,
    color: colors.textMuted,
    background: colors.card,
    transition: "all 0.2s",
    flexShrink: 0,
  },
  stepCircleActive: {
    borderColor: colors.accent,
    color: colors.accent,
    boxShadow: `0 0 12px ${colors.accentGlow}`,
  },
  stepCircleDone: {
    background: colors.accent,
    borderColor: colors.accent,
    color: "#fff",
  },
  stepLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: colors.textMuted,
    transition: "color 0.2s",
  },
  stepLabelActive: {
    color: colors.text,
  },
  stepLine: {
    width: 40,
    height: 2,
    background: colors.inputBorder,
    margin: "0 6px",
    borderRadius: 1,
    transition: "background 0.2s",
  },
  stepLineActive: {
    background: colors.accent,
  },

  // Form
  stepInner: {
    background: colors.card,
    border: `1px solid ${colors.cardBorder}`,
    borderRadius: 14,
    padding: "28px 24px",
    marginBottom: 20,
  },
  sectionLabel: {
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: 2,
    textTransform: "uppercase",
    color: colors.accentSoft,
    marginBottom: 16,
    marginTop: 8,
  },
  fieldGroup: {
    marginBottom: 16,
    flex: 1,
  },
  label: {
    display: "block",
    fontSize: 13,
    fontWeight: 600,
    color: colors.textMuted,
    marginBottom: 6,
  },
  req: {
    color: colors.accent,
  },
  input: {
    width: "100%",
    padding: "10px 14px",
    fontSize: 15,
    fontFamily: font,
    background: colors.input,
    border: `1px solid ${colors.inputBorder}`,
    borderRadius: 8,
    color: colors.text,
    outline: "none",
    transition: "border-color 0.2s",
    boxSizing: "border-box",
  },
  select: {
    width: "100%",
    padding: "10px 14px",
    fontSize: 15,
    fontFamily: font,
    background: colors.input,
    border: `1px solid ${colors.inputBorder}`,
    borderRadius: 8,
    color: colors.text,
    outline: "none",
    boxSizing: "border-box",
    appearance: "none",
  },
  row: {
    display: "flex",
    gap: 14,
    flexWrap: "wrap",
  },
  radioRow: {
    display: "flex",
    gap: 8,
  },
  radioBtn: {
    padding: "8px 18px",
    fontSize: 14,
    fontWeight: 600,
    fontFamily: font,
    border: `1px solid ${colors.inputBorder}`,
    borderRadius: 8,
    background: colors.input,
    color: colors.textMuted,
    cursor: "pointer",
    transition: "all 0.15s",
  },
  radioBtnActive: {
    borderColor: colors.accent,
    color: colors.accent,
    background: colors.accentGlow,
  },
  helperText: {
    fontSize: 12.5,
    color: colors.textMuted,
    marginTop: -8,
    marginBottom: 16,
    lineHeight: 1.5,
    fontStyle: "italic",
  },

  // Waiver
  waiverBox: {
    background: colors.input,
    border: `1px solid ${colors.inputBorder}`,
    borderRadius: 10,
    padding: 20,
    maxHeight: 260,
    overflow: "auto",
    marginBottom: 20,
  },
  waiverText: {
    fontSize: 12.5,
    lineHeight: 1.7,
    color: colors.textMuted,
    whiteSpace: "pre-wrap",
    fontFamily: font,
    margin: 0,
  },
  checkRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
    marginBottom: 18,
    cursor: "pointer",
  },
  checkbox: {
    width: 22,
    height: 22,
    borderRadius: 5,
    border: `2px solid ${colors.inputBorder}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    marginTop: 1,
    transition: "all 0.15s",
  },
  checkboxChecked: {
    background: colors.accent,
    borderColor: colors.accent,
  },
  checkLabel: {
    fontSize: 14,
    color: colors.text,
    lineHeight: 1.5,
  },

  // Payment
  summaryCard: {
    background: colors.input,
    border: `1px solid ${colors.inputBorder}`,
    borderRadius: 10,
    padding: 18,
    marginBottom: 24,
  },
  summaryRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "8px 0",
    borderBottom: `1px solid ${colors.cardBorder}`,
  },
  summaryLabel: {
    fontSize: 13,
    color: colors.textMuted,
    fontWeight: 600,
  },
  summaryValue: {
    fontSize: 15,
    color: colors.text,
    fontWeight: 600,
  },
  campusBadge: {
    padding: "3px 14px",
    borderRadius: 6,
    fontSize: 13,
    color: "#fff",
    fontWeight: 700,
    letterSpacing: 1,
  },
  paymentToggle: {
    display: "flex",
    gap: 12,
    marginBottom: 24,
  },
  paymentOptionBtn: {
    flex: 1,
    padding: "16px 14px",
    fontFamily: font,
    background: colors.input,
    border: `1px solid ${colors.inputBorder}`,
    borderRadius: 10,
    color: colors.text,
    cursor: "pointer",
    textAlign: "left",
    transition: "all 0.15s",
  },
  paymentOptionBtnActive: {
    borderColor: colors.accent,
    background: colors.accentGlow,
    boxShadow: `0 0 0 1px ${colors.accent}`,
  },
  paymentOptionLabel: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 1.5,
    textTransform: "uppercase",
    color: colors.textMuted,
    marginBottom: 4,
  },
  paymentOptionAmount: {
    fontSize: 22,
    fontWeight: 800,
    color: colors.accent,
    marginBottom: 4,
  },
  paymentOptionNote: {
    fontSize: 11.5,
    color: colors.textMuted,
    lineHeight: 1.4,
  },
  infoBox: {
    background: colors.input,
    border: `1px solid ${colors.inputBorder}`,
    borderLeft: `3px solid ${colors.accent}`,
    borderRadius: 8,
    padding: "12px 14px",
    fontSize: 13,
    color: colors.text,
    marginBottom: 20,
    lineHeight: 1.5,
  },
  paymentTimeline: {
    margin: "0 0 20px 10px",
  },
  timelineItem: {
    display: "flex",
    gap: 16,
    alignItems: "flex-start",
  },
  timelineDot: {
    width: 14,
    height: 14,
    borderRadius: "50%",
    background: colors.inputBorder,
    flexShrink: 0,
    marginTop: 4,
    border: `2px solid ${colors.cardBorder}`,
  },
  timelineConnector: {
    width: 2,
    height: 20,
    background: colors.inputBorder,
    marginLeft: 6,
    borderRadius: 1,
  },
  timelineContent: {
    paddingBottom: 4,
  },
  timelineDate: {
    fontSize: 14,
    fontWeight: 700,
    color: colors.text,
  },
  timelineAmount: {
    fontSize: 22,
    fontWeight: 800,
    color: colors.accent,
    margin: "2px 0",
  },
  timelineNote: {
    fontSize: 12,
    color: colors.textMuted,
  },
  totalBox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "14px 18px",
    background: colors.accentGlow,
    border: `1px solid ${colors.accent}33`,
    borderRadius: 10,
    marginBottom: 24,
    fontSize: 15,
    fontWeight: 700,
    color: colors.text,
  },
  totalAmount: {
    fontSize: 22,
    color: colors.accent,
    fontWeight: 800,
  },

  // Buttons
  btnRow: {
    display: "flex",
    gap: 12,
    marginTop: 8,
  },
  primaryBtn: {
    width: "100%",
    padding: "14px 24px",
    fontSize: 16,
    fontWeight: 700,
    fontFamily: font,
    background: `linear-gradient(135deg, ${colors.accent}, #1a4d9e)`,
    color: "#fff",
    border: "none",
    borderRadius: 10,
    cursor: "pointer",
    transition: "opacity 0.2s",
    marginTop: 8,
  },
  secondaryBtn: {
    padding: "14px 24px",
    fontSize: 15,
    fontWeight: 600,
    fontFamily: font,
    background: "transparent",
    color: colors.textMuted,
    border: `1px solid ${colors.inputBorder}`,
    borderRadius: 10,
    cursor: "pointer",
  },
  stripeBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    padding: "14px 24px",
    fontSize: 16,
    fontWeight: 700,
    fontFamily: font,
    background: colors.stripe,
    color: "#fff",
    border: "none",
    borderRadius: 10,
    cursor: "pointer",
    textDecoration: "none",
    transition: "opacity 0.2s",
  },
  btnDisabled: {
    opacity: 0.35,
    cursor: "not-allowed",
  },

  // Success
  successBox: {
    textAlign: "center",
    padding: "20px 0",
  },
  successIcon: {
    width: 56,
    height: 56,
    borderRadius: "50%",
    background: colors.success,
    color: "#fff",
    fontSize: 28,
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 14px",
  },
  successTitle: {
    fontSize: 22,
    fontWeight: 800,
    color: colors.success,
    marginBottom: 8,
  },
  successText: {
    fontSize: 14,
    color: colors.textMuted,
    lineHeight: 1.6,
    marginBottom: 18,
  },
  reminderText: {
    fontSize: 13,
    color: colors.textMuted,
    marginTop: 16,
    lineHeight: 1.5,
  },

  // Footer
  footer: {
    textAlign: "center",
    fontSize: 12,
    color: colors.textMuted,
    padding: "20px 0",
    letterSpacing: 0.5,
  },

  // Landing
  heroSection: {
    position: "relative",
    minHeight: 380,
    border: `1px solid ${colors.cardBorder}`,
    borderRadius: 14,
    marginBottom: 28,
    backgroundImage: "linear-gradient(180deg, rgba(5,10,20,0.45) 0%, rgba(13,37,82,0.85) 100%), url(/pics/DSC04322.JPG)",
    backgroundSize: "cover",
    backgroundPosition: "center 25%",
    overflow: "hidden",
    display: "flex",
    alignItems: "flex-end",
  },
  heroOverlay: {
    width: "100%",
    padding: "32px 24px 28px",
    textAlign: "center",
  },
  heroTagline: {
    fontSize: 32,
    fontWeight: 900,
    letterSpacing: 3,
    color: colors.accent,
    marginBottom: 12,
    textTransform: "uppercase",
    lineHeight: 1.15,
    textShadow: "0 2px 16px rgba(0,0,0,0.6)",
  },
  heroLead: {
    fontSize: 15,
    color: colors.text,
    lineHeight: 1.6,
    maxWidth: 460,
    margin: "0 auto",
    textShadow: "0 1px 8px rgba(0,0,0,0.7)",
  },
  landingSectionTitle: {
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: 2,
    textTransform: "uppercase",
    color: colors.accent,
    marginBottom: 14,
    marginTop: 8,
  },
  factsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
    gap: 12,
    marginBottom: 28,
  },
  factCard: {
    background: colors.card,
    border: `1px solid ${colors.cardBorder}`,
    borderRadius: 10,
    padding: "16px 18px",
  },
  factLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 1.5,
    textTransform: "uppercase",
    color: colors.textMuted,
    marginBottom: 6,
  },
  factValue: {
    fontSize: 16,
    fontWeight: 700,
    color: colors.text,
  },
  factSub: {
    fontSize: 12,
    color: colors.textMuted,
    marginTop: 4,
    fontStyle: "italic",
  },
  trainingGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 12,
    marginBottom: 28,
  },
  trainingCard: {
    background: colors.card,
    border: `1px solid ${colors.cardBorder}`,
    borderRadius: 10,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  trainingImage: {
    width: "100%",
    height: 160,
    backgroundSize: "cover",
    backgroundPosition: "center",
    borderBottom: `2px solid ${colors.accent}`,
  },
  trainingCardBody: {
    padding: "16px 18px 18px",
  },
  trainingDuration: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 1.5,
    color: colors.accent,
    marginBottom: 4,
  },
  trainingTitle: {
    fontSize: 18,
    fontWeight: 800,
    color: colors.text,
    marginBottom: 6,
  },
  trainingDescription: {
    fontSize: 13,
    color: colors.textMuted,
    lineHeight: 1.55,
  },
  galleryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 8,
    marginBottom: 28,
  },
  galleryItem: {
    width: "100%",
    aspectRatio: "3 / 4",
    backgroundSize: "cover",
    backgroundPosition: "center",
    borderRadius: 8,
    border: `1px solid ${colors.cardBorder}`,
  },
  pricingGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
    gap: 12,
    marginBottom: 12,
  },
  pricingCard: {
    background: colors.card,
    border: `1px solid ${colors.cardBorder}`,
    borderRadius: 10,
    padding: "20px 14px 18px",
    textAlign: "center",
    position: "relative",
  },
  pricingCardFeatured: {
    borderColor: colors.accent,
    background: colors.accentGlow,
    boxShadow: `0 0 0 1px ${colors.accent}`,
  },
  pricingBadge: {
    position: "absolute",
    top: -10,
    left: "50%",
    transform: "translateX(-50%)",
    background: colors.accent,
    color: "#fff",
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: 1.5,
    padding: "3px 10px",
    borderRadius: 4,
    textTransform: "uppercase",
  },
  pricingDuration: {
    fontSize: 12,
    fontWeight: 700,
    color: colors.textMuted,
    letterSpacing: 1,
    textTransform: "uppercase",
    marginBottom: 4,
  },
  pricingMonth: {
    fontSize: 15,
    fontWeight: 700,
    color: colors.text,
    marginBottom: 12,
  },
  pricingPrice: {
    fontSize: 26,
    fontWeight: 800,
    color: colors.accent,
    marginBottom: 4,
  },
  pricingRate: {
    fontSize: 12,
    color: colors.textMuted,
  },
  landingFinePrint: {
    fontSize: 15,
    fontWeight: 600,
    color: colors.text,
    textAlign: "center",
    padding: "12px 18px",
    background: colors.accentGlow,
    border: `1px solid ${colors.accent}55`,
    borderRadius: 8,
    marginBottom: 28,
  },
  ctaCard: {
    background: `linear-gradient(135deg, ${colors.card}, ${colors.accentGlow})`,
    border: `1px solid ${colors.accent}`,
    borderRadius: 14,
    padding: "28px 24px",
    textAlign: "center",
    marginBottom: 20,
  },
  ctaUrgency: {
    fontSize: 18,
    fontWeight: 800,
    color: colors.accent,
    marginBottom: 8,
    letterSpacing: 1.5,
    textTransform: "uppercase",
  },
  ctaSubtext: {
    fontSize: 14,
    color: colors.text,
    marginBottom: 20,
    lineHeight: 1.55,
    maxWidth: 460,
    margin: "0 auto 20px",
  },
  ctaButton: {
    width: "100%",
    padding: "16px 24px",
    fontSize: 17,
    fontWeight: 800,
    fontFamily: font,
    background: `linear-gradient(135deg, ${colors.accent}, #1a4d9e)`,
    color: "#fff",
    border: "none",
    borderRadius: 10,
    cursor: "pointer",
    letterSpacing: 1,
  },
  locationsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 12,
    marginBottom: 28,
    marginTop: 32,
  },
  locationCard: {
    background: colors.card,
    border: `1px solid ${colors.cardBorder}`,
    borderLeft: `3px solid ${colors.accent}`,
    borderRadius: 10,
    padding: "16px 18px",
  },
  locationCampus: {
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 1.5,
    textTransform: "uppercase",
    color: colors.accentSoft,
    marginBottom: 6,
  },
  locationAddress: {
    fontSize: 14,
    color: colors.text,
    lineHeight: 1.55,
    fontWeight: 600,
  },
  faqList: {
    background: colors.card,
    border: `1px solid ${colors.cardBorder}`,
    borderRadius: 10,
    padding: "4px 18px",
    marginBottom: 24,
  },
  faqItem: {
    padding: "16px 0",
    borderBottom: `1px solid ${colors.cardBorder}`,
  },
  faqQuestion: {
    fontSize: 14,
    fontWeight: 700,
    color: colors.text,
    marginBottom: 6,
    lineHeight: 1.4,
  },
  faqAnswer: {
    fontSize: 13.5,
    color: colors.textMuted,
    lineHeight: 1.55,
  },
  policyNotice: {
    background: colors.input,
    border: `1px solid ${colors.cardBorder}`,
    borderLeft: `3px solid ${colors.accent}`,
    borderRadius: 8,
    padding: "16px 18px",
    fontSize: 13,
    color: colors.text,
    lineHeight: 1.65,
    marginBottom: 20,
  },
};
